<template>
  <m-dropdown
    v-model:value="visible"
    :title="$t('dataSourcePicker.title')"
    block
    match-trigger-width
  >
    <m-focusable
      :class="['_input']"
      full-width
      type="clickable"
    >
      <div
        v-if="selectedDataSourceIcon !== ''"
        class="_icon"
      >
        <m-icon :type="selectedDataSourceIcon" />
      </div>
      <div class="_title">
        {{ dataSourceName(selectedDataSource) }}
      </div>
    </m-focusable>
    <data-source-edit-dialog
      :data-source="dataSourceToEstablish"
      @close="closeEstablish"
    />
    <template #overlay>
      <m-card
        no-padding
        list
      >
        <data-source-item
          v-for="ds in sources"
          :key="ds.uid"
          :data-source="ds"
          @click="select(ds)"
          @close="visible = false"
        />
        <m-content
          v-if="dataSources.length === 0"
          padding-xs
          class="_empty"
        >
          {{ $t('dataSource.empty', { title: goalSettings.featureNamePlural } ) }}
        </m-content>
        <m-divider xxs />
        <m-dropdown
          block
          placement="topRight"
          :title="$t('dataSourcePicker.integrations')"
        >
          <m-card-item
            icon="plus"
            close-on-click
            match-trigger-width
          >
            {{ $t('general.add') }}
          </m-card-item>
          <template #overlay>
            <m-card
              no-padding
              list
            >
              <m-card-item
                v-for="type in allowedDatasources"
                :key="type"
                :icon="dataSourceIcon({type})"
                @click="create(type)"
              >
                {{ $t(`dataSources.${camelCase(type)}`) }}
              </m-card-item>
            </m-card>
          </template>
        </m-dropdown>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script>
import DataSourceEditDialog from '@/components/datasource/DataSourceEditDialog.vue';
import DataSourceItem from '@/components/datasource/DataSourceItem.vue';
import camelCase from 'lodash-es/camelCase';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useDataSources from '@/composables/integrations/datasources';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import { computed } from 'vue';
import { dataSourceIcon } from '@/lib/data-source';
import { dataSourceType } from 'shared/constants.json';
import { sortAlphaNumeric, sortByAttr } from 'shared/lib/sort';

export default {
  name: 'DataSourcePicker',
  props: {
    selectedDataSource: {
      type: Object,
      default: () => null,
    },
  },
  emits: ['select'],
  components: { DataSourceEditDialog, DataSourceItem },
  setup() {
    const { accountSettings } = useAccountSettings();
    const { goalSettings } = useGoalSettings();
    const { dataSourceName, dataSources, createDataSource } = useDataSources();

    const allowedDatasources = computed(() => {
      const res = [];
      if (accountSettings.value.usesSheets) { res.push(dataSourceType.sheets); }
      if (accountSettings.value.usesExcel) { res.push(dataSourceType.excel); }
      if (accountSettings.value.usesJira) { res.push(dataSourceType.jira); }
      if (accountSettings.value.usesAsana) { res.push(dataSourceType.asana); }
      if (accountSettings.value.usesHubspot) { res.push(dataSourceType.hubspot); }
      if (accountSettings.value.usesSalesforce) { res.push(dataSourceType.salesforce); }
      if (accountSettings.value.usesMSPowerBI) { res.push(dataSourceType.msPowerBi); }
      if (accountSettings.value.usesMSPlanner) { res.push(dataSourceType.msPlanner); }
      if (accountSettings.value.usesAzureDevOps) { res.push(dataSourceType.azureDevOps); }
      return res.sort(sortAlphaNumeric);
    });
    const goalDataSources = computed(() => dataSources.value.filter((ds) => allowedDatasources.value.includes(ds.type)));
    return {
      goalSettings,
      allowedDatasources,

      dataSources: goalDataSources,
      dataSourceName,
      createDataSource,
    };
  },
  data() {
    return {
      camelCase,
      dataSourceType,
      visible: false,
      dataSourceToEstablish: null,
    };
  },
  computed: {
    selectedDataSourceIcon() {
      return dataSourceIcon(this.selectedDataSource);
    },
    sources() {
      return this.dataSources.map((ds) => {
        const icon = dataSourceIcon(ds);
        if (icon !== '') {
          return { ...ds, icon };
        }
        return ds;
      }).sort(sortByAttr('type', sortAlphaNumeric));
    },
  },
  methods: {
    dataSourceIcon,
    select(dataSource) {
      this.$emit('select', dataSource);
      this.close();
    },
    close() {
      this.visible = false;
    },
    create(dsType) {
      this.createDataSource({
        name: this.$t('dataSourcePicker.defaultName', { source: this.$t(`dataSources.${camelCase(dsType)}`) }),
        shared: false,
        type: dsType,
      }).then((dataSource) => {
        this.$emit('select', dataSource);
        this.dataSourceToEstablish = dataSource;
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    closeEstablish() {
      this.dataSourceToEstablish = null;
      this.close();
    },
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  ._input {
    display: flex;
    align-items: center;
    margin-top: .4rem;

    ._icon {
      margin-top: -.1rem;
      margin-right: .4rem;
    }

    ._title {
      width: 100%;
    }
  }

  ._empty {
    color: $font-color-secondary;
  }
</style>
